import React, { useCallback } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import type { CardProps } from '../../../types';

const styles = cssMap({
	card: {
		position: 'relative',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		height: '100%',
		transition:
			'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
	},
	interactive: {
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			boxShadow: token('elevation.shadow.overlay'),
			transform: 'translateY(-1px)',
		},
		// @ts-expect-error -- strict cssMap API does not model `:has()` selectors
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:has([data-card-pressable]:active)': {
			backgroundColor: token('elevation.surface.pressed'),
			boxShadow: token('elevation.shadow.raised'),
		},
	},
	surface: {
		boxSizing: 'border-box',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	pressable: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.0'),
		insetInlineStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		zIndex: 100,
		borderRadius: token('radius.xlarge'),
		backgroundColor: 'transparent',
	},
	content: {
		position: 'relative',
		height: '100%',
		width: '100%',
		display: 'grid',
		gridTemplateColumns: 'auto',
		gridTemplateRows: 'auto auto 1fr auto',
		gridTemplateAreas: '"cover" "content" "metadata" "footer"',
		rowGap: token('space.150'),
	},
});

export const Card = ({
	children,
	ariaLabel,
	onClick,
	isDisabled = false,
	testId,
	xcss,
	...boxProps
}: CardProps): React.JSX.Element => {
	const handleClick = useCallback(() => {
		onClick?.();
	}, [onClick]);

	return (
		<Box
			{...boxProps}
			xcss={cx(styles.card, Boolean(onClick) && !isDisabled && styles.interactive, xcss)}
			testId={testId}
		>
			{onClick ? (
				<Pressable
					xcss={styles.pressable}
					onClick={handleClick}
					aria-label={ariaLabel}
					isDisabled={isDisabled}
					data-card-pressable
				/>
			) : null}
			<Box xcss={styles.surface}>
				<Stack xcss={styles.content} space="space.200">
					{children}
				</Stack>
			</Box>
		</Box>
	);
};
